import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';
import { AuthService, BlockAbstractComponent, PageService, ParserService } from '@gallaghercp/core-frontend';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

interface UserScoringData {
    mostRecentCollection: any;
    userCompletedItems: { dataRecord: string; friendlyName: string; points: number }[];
    userPendingItems: { dataRecord: string; friendlyName: string; points: number; defaultScore: number | string }[];
    totalPoints: number;
    userLevel: {
        label: string;
        min: number;
        max: number;
        content: string;
        data?: { image_url?: string; image?: HTMLImageElement };
    } | null;
}

@Component({
    selector: 'core-block-user-scoring',
    templateUrl: './block-user-scoring.html',
    styleUrls: ['./block-user-scoring.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlockUserScoringComponent extends BlockAbstractComponent implements OnInit {
    protected override parser: ParserService;
    protected override pages: PageService;
    protected override auth: AuthService;
    protected breakpoint = inject(BreakpointObserver);
    protected http = inject(HttpClient);

    userScoring$!: Observable<UserScoringData>;

    constructor() {
        const parser = inject(ParserService);
        const pages = inject(PageService);
        const auth = inject(AuthService);

        super(parser, auth, pages);

        this.parser = parser;
        this.pages = pages;
        this.auth = auth;
    }

    override ngOnInit() {
        super.ngOnInit();
        this.userScoring$ = this.getUserCollections(this.block.data.chosenCollection).pipe(
            map(collections => this.processUserScoringData(collections)),
        );
    }

    // Coerces an unknown value into an allowed defaultScore type.
    // Allowed: number | string; otherwise falls back to 0.
    // Using a helper keeps the mapping logic concise and readable.
    private coerceDefaultScore(value: unknown): number | string {
        return typeof value === 'number' || typeof value === 'string' ? value : 0;
    }

    processUserScoringData(collections: any[]): UserScoringData {
        // Find the most recent collection
        const mostRecentCollection = this.getMostRecentCollection(collections);

        if (!mostRecentCollection || !mostRecentCollection.content || !mostRecentCollection.content[0]) {
            return {
                mostRecentCollection: null,
                userCompletedItems: [],
                userPendingItems: [],
                totalPoints: 0,
                userLevel: null,
            };
        }

        const collectionData = mostRecentCollection.content[0];
        const mappings = this.block.data.mappings || [];

        const userCompletedItems: { dataRecord: string; friendlyName: string; points: number }[] = [];
        const userPendingItems: {
            dataRecord: string;
            friendlyName: string;
            points: number;
            defaultScore: number | string;
        }[] = [];

        // Process mappings
        mappings.forEach((mapping: any) => {
            const dataRecord = mapping.dataRecord;
            const friendlyName = mapping.dataRecordFriendlyName;
            const points = parseFloat(collectionData[dataRecord] || '0');

            const item = { dataRecord, friendlyName, points };
            // defaultScore can be a number or a string (e.g., "5-10").
            const defaultScore: number | string = this.coerceDefaultScore(mapping?.defaultScore);

            if (points > 0) {
                userCompletedItems.push(item);
            } else {
                userPendingItems.push({ ...item, defaultScore });
            }
        });

        // Get total points
        const totalPoints = parseFloat(collectionData.TOTAL_POINTS || '0');

        // Determine user level
        const userLevel = this.getUserLevel(totalPoints);

        return {
            mostRecentCollection,
            userCompletedItems,
            userPendingItems,
            totalPoints,
            userLevel,
        };
    }

    getMostRecentCollection(collections: any[]): any {
        if (!collections || collections.length === 0) return null;

        // Sort by dateModified descending, then by id descending
        return collections.sort((a, b) => {
            if (b.dateModified !== a.dateModified) {
                return b.dateModified - a.dateModified;
            }
            return b.id - a.id;
        })[0];
    }

    getUserLevel(totalPoints: number): {
        label: string;
        min: number;
        max: number;
        content: string;
        data?: { image_url?: string; image?: HTMLImageElement };
    } | null {
        const levels = this.block.data.levels || [];

        for (const level of levels) {
            if (totalPoints >= level.min && totalPoints <= level.max) {
                return level;
            }
        }

        return null;
    }

    getUserCollections(typeId: number, identifiers?: unknown | null, includeContent?: boolean) {
        let params = new HttpParams().set('content', includeContent ? 'true' : 'false');
        if (identifiers !== null && identifiers !== undefined) {
            const identifiersJson = JSON.stringify(identifiers || {});
            params = params.set('identifiers', identifiersJson);
        }

        params = params.set('interact', 'false').set('typeId', typeId);
        return this.http.get('user-collections', { params: params }).pipe(
            map((response: any) => {
                return response;
            }),
        );
    }
}
